<template>
    <div class="line-timeline">
        <div class="d-flex">
            <h3 class="line-header">{{ header }}</h3>
            <div v-if="lineData" class="stats-row">
                <div class="stat-card stat-blue">
                    <div class="stat-label">UPTIME</div>
                    <div class="stat-value">{{ numberWithCommas(lineData.totalLine, 1) }}hrs</div>
                    <div class="stat-sub">est. {{ numberWithCommas(lineData.totalEstimated, 1) }}hrs</div>
                </div>
                <div class="stat-card stat-amber">
                    <div class="stat-label">CLR CHANGES</div>
                    <div class="stat-value">{{ numberWithCommas(lineData.totalColourChange, 1) }}hrs</div>
                    <div class="stat-sub">est. {{ numberWithCommas(lineData.totalEstimatedColourChange, 1) }}m</div>
                </div>
                <div class="stat-card stat-red">
                    <div class="stat-label">BREAK</div>
                    <div class="stat-value">{{ numberWithCommas(lineData.totalBreak, 1) }}hrs</div>
                    <div class="stat-sub">est. {{ numberWithCommas(lineData.totalEstimatedBreak, 1) }}hrs</div>
                </div>
                <div class="stat-card stat-grey">
                    <div class="stat-label">OUTSTANDING</div>
                    <div class="stat-value">{{ numberWithCommas(lineData.totalEstimatedOutstanding, 1) }}hrs</div>
                </div>
            </div>
        </div>
        <div v-if="lineData" class="timeline-scroll-container">
            <div class="timeline-track">
                <!-- Start dot -->
                <div class="timeline-start-marker">
                    <div class="start-dot"></div>
                    <div class="start-time" v-if="lineData.startTime">{{ $dayjs(lineData.startTime).format('h:mma') }}</div>
                    <div class="start-time" v-else>-</div>
                </div>

                <!-- Completed entries -->
                <div v-for="(entry, index) in lineData.entries"
                     :key="'c-' + index"
                     class="timeline-entry"
                     :style="completedEntryStyle(entry)">
                    <div class="entry-top-bar" :style="topBarStyle(entry.type)"></div>
                    <div class="entry-times">
                        <span>{{ $dayjs(entry.startTime).format('h:mma') }}</span>
                        <span>{{ $dayjs(entry.endTime).format('h:mma') }}</span>
                    </div>
                    <div class="entry-body">
                        <div class="entry-label">
                            <template v-if="entry.type == 1">
                                <div class="entry-name">{{ entry.description }}</div>
                                <div v-if="entry.contact" class="entry-sub">{{ entry.contact }}</div>
                            </template>
                            <template v-else-if="entry.type == 2">
                                <div class="entry-sub">Colour Change</div>
                            </template>
                            <template v-else-if="entry.type == 3">
                                <div class="entry-name">Break</div>
                            </template>
                        </div>
                        <v-progress-circular v-if="entry.status == 2"
                                             indeterminate size="14" width="2"
                                             color="orange"
                                             class="ms-1 flex-shrink-0"></v-progress-circular>
                        <v-icon v-else-if="entry.status == 3"
                                icon="mdi-pause-circle-outline"
                                size="small"
                                class="ms-1 flex-shrink-0"></v-icon>
                    </div>
                    <div class="entry-duration">
                        <v-chip v-if="timeDelta(entry) !== null"
                                size="x-small"
                                variant="elevated"
                                :color="timeDelta(entry) >= 0 ? 'green-darken-1' : 'red-darken-1'"
                                class="ms-1 delta-chip">
                            {{ timeDelta(entry) >= 0 ? '+' : '' }}{{ numberWithCommas(timeDelta(entry), 1) }}
                        </v-chip>
                        {{ entry.lineTime }}m
                    </div>
                </div>

                <!-- Now divider -->
                <div v-if="lineData.outstandingEntries && lineData.outstandingEntries.length > 0"
                     class="timeline-now-marker">
                    <div class="now-dot"></div>
                    <div class="now-label">NOW</div>
                </div>

                <!-- Outstanding entries -->
                <div v-for="(entry, index) in lineData.outstandingEntries"
                     :key="'o-' + index"
                     class="timeline-entry outstanding-entry"
                     :style="outstandingEntryStyle(entry)">
                    <div class="entry-top-bar outstanding-bar" :style="topBarStyle(entry.type)"></div>
                    <div class="entry-times outstanding-times">
                        <v-icon icon="mdi-clock-outline" size="x-small" class="me-1"></v-icon>
                        <span>OUTSTANDING</span>
                    </div>
                    <div class="entry-body">
                        <div class="entry-label">
                            <template v-if="entry.type == 1">
                                <div class="entry-name">{{ entry.description }}</div>
                            </template>
                            <template v-else-if="entry.type == 2">
                                <div class="entry-sub">Colour Change</div>
                            </template>
                            <template v-else-if="entry.type == 3">
                                <div class="entry-name">Break</div>
                            </template>
                        </div>
                        <v-progress-circular v-if="entry.status == 2"
                                             indeterminate size="14" width="2"
                                             color="orange"
                                             class="ms-1 flex-shrink-0"></v-progress-circular>
                        <v-icon v-else-if="entry.status == 3"
                                icon="mdi-pause-circle-outline"
                                size="small"
                                class="ms-1 flex-shrink-0"></v-icon>
                    </div>
                    <div class="entry-duration">~{{ numberWithCommas(entry.estimatedLineTime, 1) }}m</div>
                </div>
            </div>
        </div>
        <div v-else class="no-data">No data</div>
    </div>
</template>
<style scoped>
    .line-timeline {
        height: 50%;
        min-width: 100%;
        display: flex;
        flex-direction: column;
    }

    .line-header {
        padding: 2px 8px;
        font-size: 13px;
        flex-shrink: 0;
        color: rgba(255, 255, 255, 0.85);
    }

    .timeline-scroll-container {
        overflow-x: auto;
        overflow-y: hidden;
        flex: 1;
        padding-bottom: 4px;
    }

    .timeline-track {
        display: flex;
        align-items: stretch;
        min-height: 50px;
        border-top: 2px solid rgba(255, 255, 255, 0.5);
        padding-top: 0;
        white-space: nowrap;
        height:80px;
    }

    /* Start dot */
    .timeline-start-marker {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex-shrink: 0;
        padding: 0 6px;
    }

    .start-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: white;
        margin-top: -6px;
        flex-shrink: 0;
    }

    .start-time {
        font-size: 10px;
        color: rgba(255, 255, 255, 0.8);
        margin-top: 2px;
        white-space: nowrap;
    }

    /* Entry blocks */
    .timeline-entry {
        display: inline-flex;
        flex-direction: column;
        min-width: 70px;
        border-right: 1px solid rgba(255, 255, 255, 0.15);
        padding: 4px 5px;
        flex-shrink: 0;
        overflow: hidden;
        vertical-align: top;
        position: relative;
    }

    .outstanding-entry {
        border: 1px dashed rgba(255, 255, 255, 0.35);
        border-top: none;
        opacity: 0.8;
    }

    .entry-top-bar {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
    }

    .outstanding-bar {
        background-image: repeating-linear-gradient(
            90deg,
            transparent,
            transparent 4px,
            rgba(255,255,255,0.6) 4px,
            rgba(255,255,255,0.6) 8px
        ) !important;
    }

    .entry-times {
        display: flex;
        justify-content: space-between;
        font-size: 9px;
        color: rgba(255, 255, 255, 0.7);
        margin-top: 4px;
        white-space: nowrap;
        gap: 4px;
    }

    .outstanding-times {
        color: rgba(255, 200, 100, 0.9);
        font-size: 9px;
        align-items: center;
        justify-content: flex-start;
        gap: 2px;
    }

    .entry-body {
        display: flex;
        align-items: flex-start;
        flex: 1;
        margin-top: 3px;
        min-width: 0;
        overflow: hidden;
    }

    .entry-label {
        min-width: 0;
        overflow: hidden;
    }

    .entry-name {
        font-size: 10px;
        font-weight: 600;
        color: white;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .entry-sub {
        font-size: 9px;
        color: rgba(255, 255, 255, 0.7);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: wrap;
    }

    .entry-duration {
        font-size: 9px;
        color: rgba(255, 255, 255, 0.6);
        margin-top: auto;
        padding-top: 2px;
        border-top: 1px solid rgba(255, 255, 255, 0.3);
        text-align: right;
    }

    /* Now marker */
    .timeline-now-marker {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex-shrink: 0;
        padding: 0 4px;
    }

    .now-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #FF7043;
        margin-top: -6px;
        flex-shrink: 0;
        box-shadow: 0 0 6px #FF7043;
    }

    .now-label {
        font-size: 9px;
        color: #FF7043;
        margin-top: 2px;
        font-weight: 700;
        white-space: nowrap;
    }

    .no-data {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.4);
        padding: 4px 8px;
    }

    /* Stats cards */
    .stats-row {
        display: flex;
        gap: 6px;
        padding: 4px 8px;
        flex-shrink: 0;
    }

    .stat-card {
        padding: 4px 8px;
        border-radius: 6px;
        min-width: 80px;
        flex-shrink: 0;
    }

    .stat-blue  { background: rgba(25, 118, 210, 0.6); }
    .stat-amber { background: rgba(255, 160, 0, 0.6); }
    .stat-red   { background: rgba(211, 47, 47, 0.6); }
    .stat-grey  { background: rgba(69, 90, 100, 0.7); }

    .stat-label {
        font-size: 8px;
        font-weight: 700;
        color: rgba(255, 255, 255, 0.7);
        letter-spacing: 0.5px;
    }

    .stat-value {
        font-size: 13px;
        font-weight: 700;
        color: white;
        line-height: 1.2;
    }

    .stat-sub {
        font-size: 8px;
        color: rgba(255, 255, 255, 0.6);
    }

    /* Delta chip */
    .delta-chip {
        font-size: 8px !important;
        height: 14px !important;
        padding: 0 4px !important;
        vertical-align: middle;
    }
</style>
<script>
    import { numberWithCommas } from "../mixins/numberMixins";
    const PX_PER_MINUTE = 3;
    const MIN_WIDTH = 100
    const ENTRY_COLOURS = {
        1: { bg: 'rgba(25, 118, 210, 0.75)', bar: '#1565C0' },   // Job - blue
        2: { bg: 'rgba(255, 160, 0, 0.75)',  bar: '#FF8F00' },   // Colour change - amber
        3: { bg: 'rgba(211, 47, 47, 0.75)',  bar: '#C62828' },   // Break - red
    };
    const OUTSTANDING_COLOURS = {
        1: { bg: 'rgba(25, 118, 210, 0.6)',  bar: '#1565C0' },
        2: { bg: 'rgba(255, 160, 0, 0.6)',   bar: '#FF8F00' },
        3: { bg: 'rgba(211, 47, 47, 0.6)',   bar: '#C62828' },
    };
    function initialState() {
        return {
            loading: false
        };
    }
    export default {
        name: "DayLineTimeline",
        data: function () {
            return initialState();
        },
        mounted: function () {
        },
        props: {
            lineData: Object,
            header: String
        },
        methods: {
            numberWithCommas,
            entryIcon(entry) {
                switch (entry.type) {
                    case 1: return "mdi-spray";
                    case 2: return "mdi-pail-outline";
                    case 3: return "mdi-food-fork-drink";
                }
            },
            entryWidthPx(minutes) {
                return Math.max(MIN_WIDTH, (minutes || 0) * PX_PER_MINUTE);
            },
            completedEntryStyle(entry) {
                const colours = ENTRY_COLOURS[entry.type] || ENTRY_COLOURS[1];
                return {
                    width: this.entryWidthPx(entry.lineTime) + 'px',
                    backgroundColor: colours.bg,
                };
            },
            outstandingEntryStyle(entry) {
                const colours = OUTSTANDING_COLOURS[entry.type] || OUTSTANDING_COLOURS[1];
                return {
                    width: this.entryWidthPx(entry.estimatedLineTime) + 'px',
                    backgroundColor: colours.bg,
                };
            },
            topBarStyle(type) {
                const colours = ENTRY_COLOURS[type] || ENTRY_COLOURS[1];
                return { backgroundColor: colours.bar };
            },
            timeDelta(entry) {
                if (entry.estimatedLineTime != null && entry.lineTime != null) {
                    return Math.round((entry.estimatedLineTime - entry.lineTime) * 10) / 10;
                }
                return null;
            },
        },
        computed: {
        }
    };
</script>
